import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Apple-friendly: continuous radius, instant press scale (.pressable), short ease-out
  "pressable inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium tracking-[-0.01em] ring-offset-background transition-[transform,color,background-color,box-shadow,border-color] duration-[var(--duration-press)] ease-[var(--ease-out)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary-light active:bg-primary-darker",
        /** Purple pill — app nav, period filters (Analytics), etc. (use with size="nav") */
        "nav-pill":
          "border-0 bg-nav-pill text-nav-pill-foreground shadow-sm hover:bg-[hsl(var(--nav-pill)/0.92)] focus-visible:ring-2 focus-visible:ring-nav-pill focus-visible:ring-offset-2",
        "nav-pill-ghost":
          "border-0 bg-transparent text-foreground hover:bg-muted hover:text-foreground",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        // Use muted + foreground on hover: accent-foreground is white (brand), which breaks on light surfaces.
        outline:
          "border border-border bg-background text-foreground hover:bg-muted hover:text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground hover:bg-primary-light font-semibold shadow-elevated hover:shadow-xl",
        "hero-outline":
          "border-2 border-primary-foreground text-primary-foreground backdrop-blur-sm hover:bg-primary-foreground hover:text-primary",
        "hero-gold": "bg-gradient-gold text-primary-foreground font-bold shadow-gold hover:shadow-xl",
        glass:
          "border border-glass-border bg-glass-bg text-primary-foreground shadow-material backdrop-blur-md hover:bg-[hsl(0_0%_100%/0.18)]",
        sky: "bg-sky text-primary hover:bg-sky-dark",
        success: "bg-success text-success-foreground shadow-sm hover:bg-success/90",
        warning: "bg-warning text-warning-foreground shadow-sm hover:bg-warning/90",
        /** Soft pink CTA — accent brand (rare / promotional) */
        accent: "bg-accent text-accent-foreground shadow-sm hover:bg-accent-gold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-xl px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10 rounded-xl",
        /** ~36px height, pill shape — nav headers, segments */
        nav: "h-9 min-h-9 rounded-full px-3 text-xs font-medium sm:px-4 sm:text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
